/**
 * GettingStartedChecklist — dashboard activation widget.
 *
 * Fetches /api/user/checklist which computes 5 activation moments
 * (survey, avatar, first course, first topic, first DM) based on the
 * user's actual data. Each item has a "Do it now" CTA that deep-links
 * to the relevant feature.
 *
 * The widget hides itself once every item is done.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { CheckCircle2, Circle, Sparkles, ArrowRight, X } from 'lucide-react';

const STORAGE_DISMISS = 'getting_started_dismissed_v1';

const GettingStartedChecklist = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(STORAGE_DISMISS)) setDismissed(true);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user || dismissed) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/user/checklist`);
        if (alive) setData(r.data);
      } catch { /* silent */ }
    };
    load();
  }, [API, user, dismissed]);

  if (dismissed || !data) return null;
  if (data.all_done) return null;

  const pct = Math.round((data.completed / data.total) * 100);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_DISMISS, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <section
      className="pro-card p-5 bg-card border border-border rounded-2xl"
      data-testid="getting-started-checklist"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="eyebrow text-primary">Getting started</div>
          <div className="font-heading text-base font-extrabold text-foreground leading-tight">
            {data.completed} of {data.total} done — nice progress
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss checklist"
          className="text-muted-foreground hover:text-foreground p-1 rounded"
          data-testid="getting-started-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden="true">
        <div
          className="h-full bg-primary transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="getting-started-progress"
        />
      </div>

      <ul className="mt-4 space-y-2" data-testid="getting-started-items">
        {data.items.map((item) => (
          <li
            key={item.key}
            className={`flex items-center gap-3 rounded-xl px-3 py-2 transition-colors ${
              item.done ? 'bg-emerald-500/5' : 'bg-muted/40 hover:bg-muted'
            }`}
            data-testid={`checklist-item-${item.key}`}
          >
            {item.done
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              : <Circle className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-bold ${item.done ? 'text-emerald-700 line-through decoration-emerald-700/40' : 'text-foreground'}`}>
                {item.label}
              </div>
              {!item.done && (
                <div className="text-[11px] text-muted-foreground truncate">{item.description}</div>
              )}
            </div>
            {!item.done && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(item.action_href)}
                className="rounded-full text-[11px] font-bold flex-shrink-0"
                data-testid={`checklist-action-${item.key}`}
              >
                {item.action_label} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default GettingStartedChecklist;
